import { router } from '@inertiajs/react'
import { Star } from 'lucide-react'
import { useState } from 'react'
import { toast } from 'sonner'
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from '@/components/ui/Card'
import { Input } from '@/components/ui/Input'
import { Label } from '@/components/ui/Label'
import { Button } from '@/components/ui/Button'
import { Switch } from '@/components/ui/Switch'
import { useRouteParams } from '@/hooks/useRoutes'
import routes from '@/routeHelpers'

interface CsatSettings {
  enabled: boolean
  delay_minutes: number
  request_subject: string
  request_body: string
  thank_you_message: string
}

interface CsatSettingsCardProps {
  csat: CsatSettings
  entitled: boolean
}

const textareaClass =
  'w-full min-h-[80px] rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'

export function CsatSettingsCard({ csat, entitled }: CsatSettingsCardProps) {
  const { withAccount } = useRouteParams()
  const [form, setForm] = useState<CsatSettings>(csat)
  const [saving, setSaving] = useState(false)

  const update = <K extends keyof CsatSettings>(key: K, value: CsatSettings[K]) => {
    setForm((prev) => ({ ...prev, [key]: value }))
  }

  const save = () => {
    setSaving(true)
    router.patch(
      routes.settings.updateCsat.path(withAccount()),
      {
        csat: {
          enabled: form.enabled,
          delay_minutes: form.delay_minutes,
          request_subject: form.request_subject,
          request_body: form.request_body,
          thank_you_message: form.thank_you_message,
        },
      },
      {
        preserveScroll: true,
        onSuccess: () => toast.success('Customer satisfaction settings saved'),
        onFinish: () => setSaving(false),
      },
    )
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Star className="size-5" />
          Customer satisfaction
        </CardTitle>
        <CardDescription>
          Email visitors a 1–5 star rating after a conversation is resolved, then track the
          results in Analytics.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {!entitled && (
          <p className="rounded-md border border-border bg-muted/40 px-3 py-2 text-sm text-muted-foreground">
            CSAT isn't included in your current plan. Upgrade to start collecting ratings.
          </p>
        )}

        <div className="flex items-center justify-between gap-4">
          <div>
            <Label htmlFor="csat-enabled">Send CSAT requests</Label>
            <p className="text-xs text-muted-foreground mt-1">
              When on, a feedback email is sent after each conversation is resolved.
            </p>
          </div>
          <Switch
            id="csat-enabled"
            checked={form.enabled}
            onCheckedChange={(v) => update('enabled', v)}
            disabled={!entitled}
            className="cursor-pointer"
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="csat-delay">Send delay (minutes after resolved)</Label>
          <Input
            id="csat-delay"
            type="number"
            min={1}
            max={1440}
            value={form.delay_minutes}
            onChange={(e) => update('delay_minutes', Number(e.target.value))}
            disabled={!entitled}
            className="max-w-[160px]"
          />
          <p className="text-xs text-muted-foreground">
            Reopening a conversation before the delay elapses cancels its CSAT email.
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="csat-subject">Email subject</Label>
          <Input
            id="csat-subject"
            value={form.request_subject}
            onChange={(e) => update('request_subject', e.target.value)}
            disabled={!entitled}
          />
        </div>

        <div className="space-y-2">
          <Label htmlFor="csat-body">Email message</Label>
          <textarea
            id="csat-body"
            className={textareaClass}
            value={form.request_body}
            onChange={(e) => update('request_body', e.target.value)}
            disabled={!entitled}
          />
          <p className="text-xs text-muted-foreground">
            Available variables: <code>{'{visitor_name}'}</code>, <code>{'{account_name}'}</code>,{' '}
            <code>{'{agent_name}'}</code>
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="csat-thanks">Thank-you message</Label>
          <textarea
            id="csat-thanks"
            className={textareaClass}
            value={form.thank_you_message}
            onChange={(e) => update('thank_you_message', e.target.value)}
            disabled={!entitled}
          />
          <p className="text-xs text-muted-foreground">
            Shown on the landing page after a visitor submits their rating.
          </p>
        </div>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || !entitled} className="cursor-pointer">
            {saving ? 'Saving…' : 'Save changes'}
          </Button>
        </div>
      </CardContent>
    </Card>
  )
}

export default CsatSettingsCard
